import React from 'react';
import styled, { css } from 'styled-components';
import { connect } from 'react-redux';
import propTypes from 'prop-types';
import {
  SquareCheckMarkColored,
  Square,
} from '../../../../../../editJournal/toolset/assets/icons';
import Actions from '../../../../../../../../../store/actions';
import setDocumentData from '../OrganizationDetails/setDocumentData';
import {
  SUBTRACT_SELECT_ALL,
  ADD_SELECT_ALL,
  ADD_ROLES_COUNT,
  SUBTRACT_ROLES_COUNT,
} from '../../../../../../../../../constants/slate/documentOps';
import { FormatTypes } from '../../../../../../../../../constants/templateDictionaries';
import breakpointsMinWidth from '../../../../../../../../../styling/breakpointsMinWidth';

const ExportWrapper = styled.span`
  pointer-events: auto;
  cursor: pointer;

  ${(props) => {
    const { nodeType, position, isTitleEditing } = props;
    const commonStyle = `position: absolute;left: 0;`;
    if (position) {
      return css`
        bottom: 4px;
        left: 4px;
        position: absolute;
      `;
    }

    if (nodeType === 'summary') {
      return css`
        ${commonStyle}
        top: ${isTitleEditing ? '-42px' : '-38px'};

        @media ${breakpointsMinWidth.tabletSm} {
          top: ${isTitleEditing ? '-48px' : '-34px'};
        }
        @media ${breakpointsMinWidth.tabletMd} {
          top: ${isTitleEditing ? '-48px' : '-42px'};
        }
        @media ${breakpointsMinWidth.desktopSm} {
          top: ${isTitleEditing ? '-48px' : '-44px'};
        }
        @media ${breakpointsMinWidth.desktopMd} {
          top: ${isTitleEditing ? '-48px' : '-46px'};
        }
      `;
    }

    return null;
  }}
`;

const Export = (props) => {
  const { node } = props;

  const { data, key: nodeKey, type: nodeType } = node;
  const toExport = data.get('toExport') || false;

  const handleExport = () => {
    const { editor } = props;

    const dataJSON = data.toJSON();
    editor.moveToStartOfNode(node).setNodeByKey(nodeKey, {
      data: {
        ...dataJSON,
        toExport: !toExport,
      },
    });

    const {
      value: { document: currentDocument },
    } = editor;
    const { data: currentDocumentData } = currentDocument;
    let documentRolesCount = currentDocumentData.get('rolesCount');

    const organization = currentDocument.getParent(nodeKey);
    const { nodes: organizationNodes, data: organizationData } = organization;
    const organizationDataJSON = organizationData.toJSON();
    let { selectAll } = organizationDataJSON;
    const prevSelectAll = selectAll;
    let organizationToExport;

    if (toExport) {
      selectAll -= 1;
      organizationToExport = !!selectAll;
      documentRolesCount -= 1;
    } else {
      selectAll += 1;
      organizationToExport = true;
      documentRolesCount += 1;
    }

    /**
     * Use case: Update select all mark on organization details
     */
    editor.moveToStartOfNode(organization).setNodeByKey(organization.key, {
      data: {
        ...organizationDataJSON,
        toExport: organizationToExport,
        selectAll,
      },
    });

    /**
     * Use case: Update left pane check mark
     */
    const {
      value: { document: updatedDocument },
    } = editor;

    /**
     * If previous roles are completely selected & we're unchecking one of them, we need to subtract 1 the selectAll of document
     * If previous roles are not completely selected & we're checking one and making completely selected, then we need to add 1 the selectAll of document
     */
    const organizationNodesSize = organizationNodes.size;
    const rolesSize = organizationNodesSize - 1;
    let documentUpdateType;
    if (prevSelectAll === rolesSize) {
      documentUpdateType = SUBTRACT_SELECT_ALL;
    } else if (selectAll === rolesSize) {
      documentUpdateType = ADD_SELECT_ALL;
    } else if (selectAll > prevSelectAll) {
      documentUpdateType = ADD_ROLES_COUNT;
    } else if (selectAll < prevSelectAll) {
      documentUpdateType = SUBTRACT_ROLES_COUNT;
    }

    /**
     * TODO: Make this a util
     */
    setDocumentData({
      selectAllCount: selectAll,
      documentRolesCount,
      props,
      type: node.type,
      currentDocument: updatedDocument,
      editor,
      documentUpdateType,
    });
    // editor.select(prevSelection);
    editor.blur();
  };

  const updateExport = (e) => {
    e.preventDefault();

    const { editor } = props;
    const dataJSON = data.toJSON();
    editor.setNodeByKey(nodeKey, {
      data: {
        ...dataJSON,
        toExport: !toExport,
      },
    });

    const {
      value: { document },
    } = editor;
    const documentDataJSON = document.data.toJSON();
    let { selectAll, toExport: documentExport } = documentDataJSON;
    selectAll = toExport ? selectAll - 1 : selectAll + 1;
    documentExport = selectAll > 0;

    /**
     * Setting document data
     */
    editor.setNodeByKey(document.key, {
      data: {
        ...documentDataJSON,
        selectAll,
        toExport: documentExport,
      },
    });
  };

  const clickHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (nodeType === 'skill_title') handleExport(e);
    else updateExport(e);
  };

  const {
    componentKey,
    dataTestId,
    subscriptionStatus,
    position,
    isTitleEditing,
  } = props;

  return (
    <ExportWrapper
      componentKey={componentKey}
      onMouseDown={clickHandler}
      nodeType={nodeType}
      position={position}
      subscriptionStatus={subscriptionStatus}
      dataTestId={`${dataTestId}-export`}
      contentEditable={false}
      isTitleEditing={isTitleEditing}
    >
      {
        {
          true: (
            <SquareCheckMarkColored
              width="16px"
              height="16px"
              dataTestId={`${dataTestId}-check`}
            />
          ),
          false: (
            <Square
              width="16px"
              height="16px"
              dataTestId={`${dataTestId}-uncheck`}
            />
          ),
        }[toExport]
      }
    </ExportWrapper>
  );
};

Export.propTypes = {
  componentKey: propTypes.string.isRequired,
  node: propTypes.objectOf(propTypes.object).isRequired,
  editor: propTypes.shape({
    resolveController: propTypes.func,
  }).isRequired,
};

const mapStateToProps = (state, ownProps) => {
  const { formatType } = ownProps;

  let userPreferences;
  if (formatType === FormatTypes.RESUME) {
    ({ userPreferences } = state.editResumeFormReducer);
  }
  return {
    userPreferences,
  };
};

const mapDispatchToProps = (dispatch) => ({
  setUserPreferences: (params) =>
    dispatch(Actions.editDocumentFormActions.updateUserPreferences(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Export);
